import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

export const PERSONAL_AUTH_PROVIDERS = Object.freeze({
  DC_GALLOG: 'dc_gallog',
  ARCA: 'arca',
});

const ROTATED_LOGIN_CODE_STORAGE_KEY = 'doroparty:rotated-login-code';
const ROTATED_LOGIN_CODE_TTL_MS = 30 * 60 * 1000;

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function sessionStorageRef() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function storePendingRotatedLoginCode(loginCode) {
  const code = String(loginCode || '').trim();
  const storage = sessionStorageRef();
  if (!code || !storage) return;
  storage.setItem(ROTATED_LOGIN_CODE_STORAGE_KEY, JSON.stringify({
    loginCode: code,
    expiresAt: Date.now() + ROTATED_LOGIN_CODE_TTL_MS,
  }));
}

export function readPendingRotatedLoginCode() {
  const storage = sessionStorageRef();
  if (!storage) return '';
  try {
    const raw = storage.getItem(ROTATED_LOGIN_CODE_STORAGE_KEY);
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    const loginCode = String(parsed?.loginCode || '').trim();
    if (!loginCode || Number(parsed?.expiresAt || 0) <= Date.now()) {
      storage.removeItem(ROTATED_LOGIN_CODE_STORAGE_KEY);
      return '';
    }
    return loginCode;
  } catch {
    storage.removeItem(ROTATED_LOGIN_CODE_STORAGE_KEY);
    return '';
  }
}

export function clearPendingRotatedLoginCode() {
  sessionStorageRef()?.removeItem(ROTATED_LOGIN_CODE_STORAGE_KEY);
}

function supabaseAuthStoragePrefix() {
  try {
    const projectRef = new URL(String(import.meta.env.VITE_SUPABASE_URL || '')).hostname.split('.')[0];
    return projectRef ? `sb-${projectRef}-auth-token` : '';
  } catch {
    return '';
  }
}

function forceClearLocalAuthStorage() {
  if (typeof window === 'undefined') return;
  const prefix = supabaseAuthStoragePrefix();
  if (!prefix) return;
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      for (let index = storage.length - 1; index >= 0; index -= 1) {
        const key = storage.key(index);
        if (key && key.startsWith(prefix)) storage.removeItem(key);
      }
    } catch {
      // Storage can be unavailable in private or restricted browser modes.
    }
  }
}

async function readFunctionError(error) {
  try {
    const payload = await error?.context?.json();
    return String(payload?.error || payload?.code || '').trim();
  } catch {
    return '';
  }
}

async function invoke(body) {
  const client = requireClient();
  const { data, error } = await client.functions.invoke('dc-gallog-auth-secure', { body });
  if (error) {
    const serverCode = await readFunctionError(error);
    throw new Error(serverCode || error.message || 'edge_function_failed');
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export function normalizeArcaIdentifier(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^https:\/\//i.test(raw)) return raw;

  const compact = raw.replace(/^@+/, '').trim();
  const match = compact.match(/^([^/\s?#]{1,80})(?:\/([0-9]{1,20}))?$/u);
  if (!match) return raw;

  const nickname = match[1];
  const semiFixedId = match[2] ? `/${match[2]}` : '';
  return `https://arca.live/u/@${encodeURIComponent(nickname)}${semiFixedId}`;
}

export async function startPersonalIdentityVerification(provider, identifier) {
  const normalizedProvider = provider === PERSONAL_AUTH_PROVIDERS.ARCA
    ? PERSONAL_AUTH_PROVIDERS.ARCA
    : PERSONAL_AUTH_PROVIDERS.DC_GALLOG;
  const rawIdentifier = String(identifier || '').trim();
  const normalizedIdentifier = normalizedProvider === PERSONAL_AUTH_PROVIDERS.ARCA
    ? normalizeArcaIdentifier(rawIdentifier)
    : rawIdentifier;
  return invoke({
    action: 'start',
    provider: normalizedProvider,
    identifier: normalizedIdentifier,
    ...(normalizedProvider === PERSONAL_AUTH_PROVIDERS.DC_GALLOG ? { gallogId: normalizedIdentifier } : { profileUrl: normalizedIdentifier }),
  });
}

export async function startGallogVerification(gallogId) {
  return startPersonalIdentityVerification(PERSONAL_AUTH_PROVIDERS.DC_GALLOG, gallogId);
}

export async function startArcaVerification(nickname) {
  return startPersonalIdentityVerification(PERSONAL_AUTH_PROVIDERS.ARCA, nickname);
}

export async function checkPersonalIdentityVerification(challengeId, provider = '') {
  return invoke({ action: 'verify', challengeId, provider });
}

export async function checkGallogVerification(challengeId) {
  return checkPersonalIdentityVerification(challengeId);
}

export async function signInWithPersonalCode(loginCode) {
  const client = requireClient();
  const data = await invoke({ action: 'login', loginCode });
  if (!data?.session?.access_token || !data?.session?.refresh_token) {
    throw new Error('로그인 세션을 발급하지 못했습니다.');
  }
  const { error } = await client.auth.setSession({
    access_token: data.session.access_token,
    refresh_token: data.session.refresh_token,
  });
  if (error) throw error;
  clearPendingRotatedLoginCode();
  return data.session;
}

export async function finishGallogSignup(result) {
  const client = requireClient();
  if (result?.linkedToCurrentAccount) {
    const { data, error } = await client.auth.getSession();
    if (error) throw error;
    if (!data?.session) throw new Error('현재 로그인 계정 연결을 확인하지 못했습니다.');
    return data.session;
  }
  if (!result?.session?.access_token || !result?.session?.refresh_token) {
    throw new Error('가입 세션을 발급하지 못했습니다.');
  }
  const { error } = await client.auth.setSession({
    access_token: result.session.access_token,
    refresh_token: result.session.refresh_token,
  });
  if (error) throw error;
  return result.session;
}

export async function loadPersonalLoginStatus() {
  const client = requireClient();
  const { data, error } = await client.rpc('get_my_personal_login_status');
  if (error) throw error;
  return Array.isArray(data) ? data[0] || null : data || null;
}

export async function rotatePersonalLoginCode() {
  const client = requireClient();
  const result = await invoke({ action: 'rotate' });
  if (!result?.loginCode) throw new Error('login_code_generation_failed');
  storePendingRotatedLoginCode(result.loginCode);
  try {
    await client.auth.signOut({ scope: 'local' });
  } catch {
    // Rotation has already revoked the server session; local storage is cleared below.
  }
  forceClearLocalAuthStorage();
  return result;
}

export function getPersonalCodeAuthError(error) {
  const message = String(error?.message || error || '인증 처리 중 오류가 발생했습니다.');
  const code = message.split(':')[0];
  const map = {
    gallog_id_invalid: 'DC 갤로그 식별코드를 정확히 입력해주세요.',
    gallog_unreachable: 'DC 갤로그에 접속하지 못했습니다. 잠시 후 다시 시도해주세요.',
    gallog_not_found: '공개된 DC 갤로그를 찾지 못했습니다.',
    verification_code_author_not_found: '인증코드는 찾았지만 작성자 갤로그를 확인할 수 없습니다. DC에 로그인한 본인 계정으로 방명록 글을 작성해주세요.',
    verification_code_author_mismatch: '인증코드를 작성한 DC 계정이 입력한 갤로그와 일치하지 않습니다. 본인 계정으로 다시 작성해주세요.',
    dc_gallog_verification_author_required: '작성자 갤로그 일치 확인 없이 DC 인증을 완료할 수 없습니다. 새 인증코드로 다시 진행해주세요.',
    dc_verification_security_migration_required: 'DC 인증 보안 설정이 아직 적용되지 않았습니다. 관리자에게 알려주세요.',
    dc_verification_challenge_read_failed: 'DC 인증 요청 정보를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.',
    arca_profile_url_invalid: '아카라이브 닉네임을 정확히 입력하거나 프로필 주소를 사용해주세요.',
    arca_profile_unreachable: '아카라이브 프로필에 접속하지 못했습니다. 잠시 후 다시 시도해주세요.',
    arca_profile_not_found: '공개된 아카라이브 사용자 프로필을 찾지 못했습니다.',
    challenge_required: '인증 요청 정보가 없습니다. 인증코드를 다시 발급해주세요.',
    challenge_not_found: '인증 요청을 찾지 못했습니다. 인증코드를 다시 발급해주세요.',
    challenge_not_pending: '이미 사용되었거나 취소된 인증코드입니다.',
    challenge_already_used: '인증 완료 후 시간이 지났습니다. 새 인증코드를 발급해주세요.',
    challenge_expired: '인증코드가 만료됐습니다. 새 코드를 발급해주세요.',
    challenge_attempt_limit: '확인 횟수를 초과했습니다. 새 인증코드를 발급해주세요.',
    verification_code_not_found: '선택한 인증 페이지에서 인증코드를 찾지 못했습니다.',
    login_code_invalid: '개인 로그인 코드 형식이 올바르지 않습니다.',
    login_code_incorrect: '개인 로그인 코드가 올바르지 않습니다.',
    login_temporarily_locked: '로그인 실패가 반복되어 15분간 잠겼습니다.',
    login_code_generation_failed: '새 개인 로그인 코드를 발급하지 못했습니다.',
    gallog_verification_required: '먼저 DC 갤로그 또는 아카라이브 본인 인증을 완료해주세요.',
    personal_identity_required: '먼저 DC 갤로그 또는 아카라이브 본인 인증을 완료해주세요.',
    gallog_account_incomplete: '본인 인증 계정 연결 정보가 불완전합니다. 인증을 다시 진행해주세요.',
    personal_auth_account_incomplete: '본인 인증 계정 연결 정보가 불완전합니다. 인증을 다시 진행해주세요.',
    auth_user_create_failed: '사이트 계정을 만들지 못했습니다. 잠시 후 다시 시도해주세요.',
    session_link_failed: '로그인 링크 생성에 실패했습니다. 잠시 후 다시 시도해주세요.',
    session_generation_failed: '로그인 세션 발급에 실패했습니다. 잠시 후 다시 시도해주세요.',
    authentication_required: '로그인이 필요합니다.',
    edge_function_failed: '인증 서버 응답을 확인하지 못했습니다. 잠시 후 다시 시도해주세요.',
  };
  return map[code] || message;
}
